import React, { FC, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { useViewerThumbnailsBatchQuery } from './__generated__/useViewerThumbnailsBatchQuery.graphql';

interface ViewerThumbnailProduct {
    code?: string | null;
    zakekeEnvironment?: string | null;
    smallImageUrl?: string | null;
}

const ViewerThumbnailsBatch: FC<{ codes: string[]; onResolved: (codes: string[], resolved: Record<string, string>) => void }> = ({ codes, onResolved }) => {
    const data = useLazyLoadQuery<useViewerThumbnailsBatchQuery>(
        graphql`
            query useViewerThumbnailsBatchQuery($codes: [String!]!) {
                damScenesByCodes(codes: $codes) {
                    code
                    assets {
                        roles
                        asset {
                            thumbnailUrl
                        }
                    }
                }
            }
        `,
        { codes }
    );

    useEffect(() => {
        const resolved: Record<string, string> = {};
        (data.damScenesByCodes ?? []).forEach((scene) => {
            if (!scene?.code) return;
            const modelAsset = scene.assets?.find((a) => a?.roles?.includes('SCENE_ASSET_ROLE_MODEL'));
            const thumbnailUrl = modelAsset?.asset?.thumbnailUrl;
            if (thumbnailUrl) resolved[scene.code] = thumbnailUrl;
        });
        onResolved(codes, resolved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return null;
};

export function useViewerThumbnails<T extends ViewerThumbnailProduct>(products: T[]) {
    const codesNeedingThumbnail = useMemo(
        () =>
            Array.from(
                new Set(
                    products.filter((p) => p.zakekeEnvironment === 'VIEWER' && !p.smallImageUrl && p.code).map((p) => p.code as string)
                )
            ),
        [products]
    );
    const codesKey = codesNeedingThumbnail.join(',');

    const [batches, setBatches] = useState<string[][]>([]);
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
    const [resolvedCodes, setResolvedCodes] = useState<Set<string>>(new Set());
    const batchedCodes = useRef<Set<string>>(new Set());

    useEffect(() => {
        const newCodes = codesNeedingThumbnail.filter((code) => !batchedCodes.current.has(code));
        if (newCodes.length === 0) return;
        newCodes.forEach((code) => batchedCodes.current.add(code));
        setBatches((prev) => [...prev, newCodes]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codesKey]);

    const handleResolved = useCallback((codes: string[], resolved: Record<string, string>) => {
        setThumbnails((prev) => ({ ...prev, ...resolved }));
        setResolvedCodes((prev) => new Set([...prev, ...codes]));
    }, []);

    const getThumbnail = useCallback((product: ViewerThumbnailProduct): string | undefined => (product.code ? thumbnails[product.code] : undefined), [thumbnails]);

    const isPending = useCallback(
        (product: ViewerThumbnailProduct): boolean => {
            if (product.zakekeEnvironment !== 'VIEWER' || product.smallImageUrl || !product.code) return false;
            return !thumbnails[product.code] && !resolvedCodes.has(product.code);
        },
        [thumbnails, resolvedCodes]
    );

    const batchNodes: ReactNode = batches.map((batch, index) => (
        <Suspense key={index} fallback={null}>
            <ViewerThumbnailsBatch codes={batch} onResolved={handleResolved} />
        </Suspense>
    ));

    return { getThumbnail, isPending, batchNodes };
}
